import axios from 'axios';
import { authService } from './authService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:42026';

export interface BusinessDataConnection {
  id: string;
  serviceType: string;
  serviceName: string;
  category: string;
  status: string;
  authMethod: string;
  config: Record<string, any>;
  createdBy: string;
  createdAt: string | null;
  verifiedAt?: string | null;
  lastError?: string | null;
}

export interface CreateConnectionPayload {
  serviceType: string;
  serviceName: string;
  category: string;
  authMethod: string;
  credentials: string;
  config: Record<string, any>;
}

class BusinessDataConnectionService {
  private getAuthHeaders() {
    const token = authService.getToken();
    return token
      ? { 'x-access-token': token, 'x-pp-client-id': 'privacypal-user-portal' }
      : {};
  }

  async getConnections(): Promise<BusinessDataConnection[]> {
    const response = await axios.get(`${API_BASE_URL}/api/business-data/connections`, {
      headers: this.getAuthHeaders(),
    });
    return response.data?.connections || [];
  }

  async createConnection(payload: CreateConnectionPayload): Promise<BusinessDataConnection> {
    const response = await axios.post(`${API_BASE_URL}/api/business-data/connections`, payload, {
      headers: this.getAuthHeaders(),
    });
    return response.data?.connection;
  }

  // Per-tenant OAuth: the customer supplies their own app (Salesforce: My
  // Domain login URL + Consumer Key/Secret; HubSpot: Client ID/secret). The
  // server stages them on a pending connection and returns the authorize URL +
  // connectionId.
  async startOAuth(
    service: string,
    appCreds: { loginUrl?: string; clientId: string; clientSecret: string; returnTo?: string },
  ): Promise<{ authorizeUrl: string; connectionId: string }> {
    const response = await axios.post(
      `${API_BASE_URL}/api/business-data/connections/oauth/${service}/start`,
      appCreds,
      { headers: this.getAuthHeaders() },
    );
    return { authorizeUrl: response.data?.authorizeUrl, connectionId: response.data?.connectionId };
  }

  // Redirect URI + scopes the customer must register in their own app.
  // optionalScopes is HubSpot only (custom objects, tickets).
  async getOAuthConfig(service: string): Promise<{ redirectUri: string; scopes: string[]; optionalScopes: string[] }> {
    const response = await axios.get(`${API_BASE_URL}/api/business-data/connections/oauth/${service}/config`, {
      headers: this.getAuthHeaders(),
    });
    return {
      redirectUri: response.data?.redirectUri,
      scopes: response.data?.scopes || [],
      optionalScopes: response.data?.optionalScopes || [],
    };
  }

  async updateConnection(id: string, data: Partial<{ config: Record<string, any>; status: string }>): Promise<BusinessDataConnection> {
    const response = await axios.put(`${API_BASE_URL}/api/business-data/connections/${id}`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data?.connection;
  }

  async deleteConnection(id: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/api/business-data/connections/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }
}

export const businessDataConnectionService = new BusinessDataConnectionService();
